import { UserRole } from "../User";
import { Timestamp } from "./Global";

export type BettingCategory = "single" | "combination" | "system";

export type CouponSelectionStatus = "open" | "won" | "lost" | "cashed_out";

export type CouponType = "live+prematch" | "live" | "prematch";

export enum CouponState {
  Open = "open",
  Settled = "settled",
}

export enum CouponStatus {
  Ongoing = "ongoing",
  Won = "won",
  Lost = "lost",
  Canceled = "canceled",
  Refunded = "refunded",
  // Closed by the player for a fixed amount (cash out). Settled and paid:
  // `totalPayout` is the amount credited; never reopened.
  CashedOut = "cashed_out",
}

export interface CouponSelectionParticipant {
  id: number;
  key?: string;
  name: string;
  score: number;
}

export interface CouponComboBet {
  id: number;
  comboId: number;
  label: string;
  odds: number;
  payout: number;
  potentialPayout: number;
  status: string;
  stake: number;
}

export interface CouponCombo {
  id: number;
  couponId: string;
  betsCount: number;
  size: number;
  stake: number;
  type: string;
  bets: CouponComboBet[];
}

export interface CouponSelectionGoal {
  /** Scoring side — matches CouponSelectionParticipant.id. */
  participantId: number;
  /** Match minute; null when the feed didn't carry one. */
  minute: number | null;
  /**
   * Stoppage minutes on top of `minute` — a 45+5 goal is `minute: 45,
   * injuryTime: 5`. Null/absent in regular play, or from a feed that folds
   * stoppage into the minute. Optional so older payloads still parse.
   */
  injuryTime?: number | null;
  /** Elapsed play time in seconds — exact feed data, always present. */
  seconds?: number;
  period: number;
  /**
   * Wall-clock moment of the goal (ISO) from the live sync's first-seen
   * timestamp; null when unknown (legacy rows, manual imports).
   */
  at?: string | null;
  /** "penalty" when the goal came from a penalty; null/absent when unknown. */
  method?: "penalty" | null;
}

export interface CouponSelectionPenalty {
  /** Team awarded the penalty — matches CouponSelectionParticipant.id. */
  participantId: number;
  minute: number | null;
  /** Stoppage minutes on top of `minute`. Null/absent in regular play. */
  injuryTime?: number | null;
  seconds?: number;
  period: number;
  /** "scored" is already shown on the goal row; only missed/unknown get a row. */
  outcome: "scored" | "missed" | "unknown";
  at?: string | null;
}

export interface CouponSelectionPeriodScore {
  id: number;
  score: number;
}

export interface CouponSelectionPeriod {
  period: number;
  shortName: string;
  /** "half" | "full_time" | "overtime" | "penalties" | ... */
  periodType: string;
  score: CouponSelectionPeriodScore[];
}

export interface CouponSelection {
  id: number;
  selectionId: string;
  selectionKey?: string;
  couponId: string;
  odds: number;
  name: string;
  label: string;
  status: CouponSelectionStatus;
  ref: number;
  stake: number;
  periodLabel?: string;
  providerId: number;
  startTime: number;
  minuteSnapshot?: number | null;
  scoreSnapshot?: string | null;
  // Matchday captured at bet time — labels a saved VR coupon's match by week.
  roundSnapshot?: number | null;
  // Event Props
  eventName?: string;
  eventCategory?: string;
  eventCategoryLabel?: string;

  // Match Props
  matchStatus: any;
  participants?: CouponSelectionParticipant[];
  currentBetStatus?: string | null;
  /**
   * The match kicked off and was then stopped for good: its periods /
   * participants hold the score at the stop. Optional like currentBetStatus —
   * a cached or pre-field response lacks it. See isAbandonedMatch.
   */
  matchAbandoned?: boolean;
  periods?: CouponSelectionPeriod[];
  /** Goal events in chronological order; empty/absent when the match has none. */
  goals?: CouponSelectionGoal[];
  penalties?: CouponSelectionPenalty[];
  eventCategoryIcon?: string;
  matchStartDate: string;
}

export type CouponLiveStatus = "won" | "lost" | "refunded";

export interface PlayerCoupon extends Timestamp {
  id: string;
  userId: number;
  category: BettingCategory;
  status: CouponStatus;
  state: CouponState;
  totalBets: number;
  totalOdds: number;
  totalBonus: number;
  totalStake: number;
  basePotentialPayout: number;
  totalPotentialPayout: number;
  totalPayout: number;
  selections: CouponSelection[];
  combos: CouponCombo[];
  cancelable: boolean;
  cancelExpiresAt: string | null;
  userIp: string;
  userName?: string;
  currentBetStatus?: CouponLiveStatus | null;
  cancellation: CouponCancellation | null;
}

export interface CouponCancellationActor {
  id: number;
  username: string;
  role: string;
}

/**
 * Why and when a closed coupon was closed. Sent only to roles allowed to see
 * it (admin / agent / dealer) and only for closed coupons; null otherwise.
 * - `manual`         — a person cancelled it; `by` is that account.
 * - `manual-unknown` — a person cancelled it, but the ledger row predates the
 *                      actor column and does not record which account.
 * - `system`         — the settlement voided it (match cancelled/postponed).
 */
export type CouponCancellation =
  | { kind: "manual"; by: CouponCancellationActor; at: string }
  | { kind: "manual-unknown"; at: string }
  | { kind: "system"; at: string };

export interface CouponUser {
  id: number;
  username: string;
  role: UserRole;
  parent: number | null;
}

export interface Coupon extends Timestamp {
  id: string;
  user: CouponUser;
  subDealer: CouponUser;
  dealer: CouponUser;
  agent: CouponUser | null;
  status: CouponStatus;
  state: CouponState;
  type: CouponType;
  category: BettingCategory;
  totalOdds: number;
  settledSelections: number;
  totalSelections: number;
  totalStake: number;
  totalPayout: number;
  totalPotentialPayout: number;
  credit: number;
  consumedCredit: number;
  name?: string;
  userIp: string | null;
  cancelable: boolean;
  cancelExpiresAt: string | null;
  currentBetStatus?: CouponLiveStatus | null;
  // Sport of the coupon (derived from its matches); null when unresolvable.
  sportCategory?: string | null;
  // Market-implied chance the coupon wins (keyset summary only). Open coupons
  // carry the live value; a settled won or lost coupon carries the value we
  // stored before its result (see `source`). null for refunded, canceled or
  // system coupons, for coupons with no stored value, for viewers other than
  // admin or a dealer whose win probability rule is on, or when the backend
  // could not compute it.
  winProbability?: CouponWinProbability | null;
}

/**
 * The least fresh price any priced leg used: the current price, the last
 * known price (a current price was unavailable), or the placement odds.
 */
export type CouponWinProbabilityBasis = "current" | "last_known" | "placement";

/**
 * Where the value comes from. "live" is computed now for an open coupon. The
 * others are stored values shown on a settled won or lost coupon:
 * "prekickoff" is our prediction before the first match started,
 * "at_placement" the prediction when a live coupon was placed, and "last"
 * the last value shown while the outcome was still open.
 */
export type CouponWinProbabilitySource =
  | "live"
  | "prekickoff"
  | "at_placement"
  | "last";

/**
 * `value` is a decimal probability in [0, 1] (6 dp). `estimated` is true when
 * at least one leg had no current price (`basis` is not "current"). `basis`
 * and `source` are optional so the panel tolerates a backend that does not
 * send them yet; an absent `source` means "live".
 */
export interface CouponWinProbability {
  value: number;
  estimated: boolean;
  basis?: CouponWinProbabilityBasis;
  source?: CouponWinProbabilitySource;
}

export interface CouponsStatement {
  type: string;
  numberOfBets: number;
  amount: number;
}

export interface CategoryCouponsStatements {
  single: CouponsStatement[];
  combination: CouponsStatement[];
  system: CouponsStatement[];
  total: CouponsStatement[];
}
